import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Calendar, Download, Loader2, MapPin, Ticket, Trophy, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const STATE = {
  active: ['Confirmed', 'bg-emerald-50 text-emerald-700'],
  pending: ['Awaiting team confirmation', 'bg-amber-50 text-amber-700'],
  payment_pending: ['Payment pending', 'bg-amber-50 text-amber-700'],
  cancelled: ['Cancelled', 'bg-red-50 text-red-700'],
  refunded: ['Refunded', 'bg-gray-100 text-gray-600'],
};
const fmtDate = (d) => {
  if (!d) return 'Date TBA';
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? d : x.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};
const venueOf = (e) => (e.event_type === 'offline' ? e.venue || e.venue_address || 'Venue TBA' : 'Online');

const Row = ({ h, onDownload, busy }) => {
  const [label, tone] = STATE[h.ticket_state] || [h.ticket_state, 'bg-gray-100 text-gray-600'];
  const e = h.event;
  return (
    <li className="px-5 py-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-5" data-testid={`my-hackathon-${h.registration_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          {e.id && !h.event_missing ? <Link to={`/events/${e.id}`} className="font-semibold text-sm text-[#0A0A0A] hover:underline truncate" data-testid={`my-hackathon-event-link-${h.registration_id}`}>{e.title}</Link>
            : <span className="font-semibold text-sm text-[#0A0A0A]">{e.title}</span>}
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${tone}`} data-testid={`my-hackathon-status-${h.registration_id}`}>{label}</span>
          {h.checked_in && <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700">Checked in</span>}
        </div>
        <p className="mt-1 text-xs text-[#52525B] flex flex-wrap gap-x-4 gap-y-1">
          <span className="inline-flex items-center gap-1"><Calendar size={12} />{fmtDate(e.event_date)}</span>
          <span className="inline-flex items-center gap-1"><MapPin size={12} />{venueOf(e)}</span>
          {e.company_name && <span>by {e.company_name}</span>}
          <span className="font-mono">#{h.ticket_code}</span>
        </p>
        {h.team && (
          <p className="mt-1 text-xs text-[#52525B] flex items-start gap-1" data-testid={`my-hackathon-team-${h.registration_id}`}>
            <Users size={12} className="mt-0.5 shrink-0" />
            <span><b className="text-[#0A0A0A]">Team {h.team.name}</b> · you're {h.team.my_role} · {h.team.members.map((m) => m.name).join(', ')}</span>
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {h.ticket_state === 'active' ? (
          <>
            <Link to={`/events/${e.id}/ticket/${h.registration_id}`} className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#E4E4E7] rounded text-xs font-semibold text-[#0A0A0A] hover:bg-[#F4F4F5]" data-testid={`my-hackathon-view-ticket-${h.registration_id}`}><Ticket size={13} /> View ticket</Link>
            <button type="button" onClick={() => onDownload(h)} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#002FA7] text-white rounded text-xs font-semibold disabled:opacity-50" data-testid={`my-hackathon-download-${h.registration_id}`}>
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} PDF
            </button>
          </>
        ) : e.id && !h.event_missing && <Link to={`/events/${e.id}`} className="text-xs font-semibold text-[#002FA7]" data-testid={`my-hackathon-manage-${h.registration_id}`}>Manage registration →</Link>}
      </div>
    </li>
  );
};

export const MyHackathonsCard = ({ token }) => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState('');
  const [all, setAll] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/codearena/me/hackathons`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setItems(r.data.hackathons)).catch(() => setItems([]));
  }, [token]);

  const download = async (h) => {
    setBusy(h.registration_id);
    try {
      const r = await axios.get(`${API}/api/codearena/me/tickets/${h.registration_id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement('a'), { href: url, download: `ticket-${h.ticket_code}.pdf` });
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error('Could not download ticket'); } finally { setBusy(''); }
  };

  return (
    <section className="bg-white border border-[#E4E4E7]" style={{ borderRadius: '2px' }} data-testid="my-hackathons-card">
      <div className="px-5 py-4 border-b border-[#E4E4E7] flex items-center justify-between gap-3">
        <h2 className="font-semibold text-sm text-[#0A0A0A] flex items-center gap-2"><Trophy size={16} className="text-[#002FA7]" /> My Hackathons & Tickets</h2>
        <Link to="/codearena/events" className="text-xs font-semibold text-[#002FA7]" data-testid="my-hackathons-browse-link">Browse events →</Link>
      </div>
      {items === null ? <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-[#A1A1AA]" size={20} /></div>
        : items.length === 0 ? <p className="px-5 py-6 text-sm text-[#52525B]" data-testid="my-hackathons-empty">You haven't registered for any hackathon or event yet.</p>
          : <>
            <ul className="divide-y divide-[#E4E4E7]">{(all ? items : items.slice(0, 4)).map((h) => <Row key={h.registration_id} h={h} onDownload={download} busy={busy === h.registration_id} />)}</ul>
            {items.length > 4 && <button type="button" onClick={() => setAll((v) => !v)} className="w-full py-3 border-t border-[#E4E4E7] text-xs font-semibold text-[#002FA7] hover:bg-[#F4F4F5]" data-testid="my-hackathons-toggle-all">{all ? 'Show less' : `Show all ${items.length}`}</button>}
          </>}
    </section>
  );
};
